import type React from 'react';

export type FieldFormatter = {
  display?: (
    v: number | undefined,
    currencyFormatter?: (val: number) => string
  ) => React.ReactNode;
  getValueProps?: (value: number) => Record<string, number>;
  getValueFromEvent?: (value: number) => number;
  max?: number;
  min?: number;
  precision?: number;
};

export type FieldFormatterKey =
  | 'discount'
  | 'percentage'
  | 'remainingStock'
  | 'rate'
  | 'discountPrice'
  | 'price'
  | 'priceDiscount'
  | 'maxBooking'
  | 'default';

const priceFormatter: FieldFormatter = {
  display: (
    v: number | undefined,
    currencyFormatter?: (val: number) => string
  ): React.ReactNode => {
    if (v === undefined) {
      return '-';
    }

    return currencyFormatter
      ? currencyFormatter(Math.floor(v))
      : Math.floor(v).toFixed(2);
  },
  getValueProps: (value: number) => ({
    value,
  }),
  getValueFromEvent: (value: number) => value,
  min: 0,
  precision: 2,
};

const percentageDisplay = (v: number | undefined): React.ReactNode => {
  return v === undefined ? '-' : `${(v * 100).toFixed(2)}%`;
};

const discountFormatter: FieldFormatter = {
  display: percentageDisplay,
  getValueProps: (value: number) => ({
    value: Math.round(value * 100 * 100) / 100,
  }),
  getValueFromEvent: (value: number) => value / 100,
  max: 100,
  min: 0,
  precision: 2,
};

export const fieldFormatters: Record<FieldFormatterKey, FieldFormatter> = {
  discount: discountFormatter,

  percentage: {
    display: percentageDisplay,
    getValueProps: (value: number) => ({
      value: value * 100,
    }),
    getValueFromEvent: (value: number) => value / 100,
    max: 100,
    min: 0,
  },

  remainingStock: {
    display: (v: number | undefined): React.ReactNode => {
      return v ?? 0;
    },
    getValueProps: (value: number) => ({
      value,
    }),
    getValueFromEvent: (value: number) => value,
    min: 0,
  },

  rate: priceFormatter,
  discountPrice: priceFormatter,

  price: {
    display: (
      v: number | undefined,
      currencyFormatter?: (val: number) => string
    ): React.ReactNode => {
      if (v === undefined) {
        return '-';
      }

      return currencyFormatter ? currencyFormatter(v) : v.toString();
    },
    getValueProps: (value: number) => ({
      value,
    }),
    getValueFromEvent: (value: number) => value,
    min: 0,
  },

  priceDiscount: discountFormatter,

  maxBooking: {
    display: (v: number | undefined): React.ReactNode => v ?? '-',
    getValueProps: (value: number) => ({ value }),
    getValueFromEvent: (value: number) => value,
    min: 0,
    precision: 0,
  },

  default: {
    display: (v: number | undefined): React.ReactNode => v ?? '-',
    getValueProps: (value: number) => ({ value }),
    getValueFromEvent: (value: number) => value,
  },
};

/**
 * Get formatter for a specific field
 * Falls back to default formatter if field-specific formatter not found
 */
export const getFieldFormatter = (
  fieldName: FieldFormatterKey
): FieldFormatter => {
  return fieldFormatters[fieldName] || fieldFormatters.default;
};
